import { useState, useRef, useEffect, useCallback } from 'react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Video, RotateCcw, Save, ArrowRight, Camera, Mic, Loader2, Users, Download, Share2, Upload } from 'lucide-react';
import { useMediaRecorder } from '@/hooks/useMediaRecorder';
import { useVoiceActivity } from '@/hooks/useVoiceActivity';
import { parseScript, getCharacters, type DialogueTurn } from '@/utils/parseScript';
import { CameraPreview } from './CameraPreview';

interface RecordingModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (file: File) => void;
  initialScript?: string; // seed the teleprompter (e.g. from the concierge)
}

type Phase = 'setup' | 'recording' | 'review';

const VOICES = [
  { id: 'Matthew', label: 'Male (Matthew)' },
  { id: 'Ruth', label: 'Female (Ruth)' },
  { id: 'Stephen', label: 'Male (Stephen)' },
  { id: 'Joanna', label: 'Female (Joanna)' },
];

// Module-level cache of synthesized reader clips, keyed by voice + line text.
// Persists for the page session so retakes of the same scene (re-clicking
// "Start Camera") reuse clips instead of paying Polly + waiting on synthesis
// again. Changing one character's voice only re-synthesizes that character's
// lines; everything else is a cache hit. Clips are small MP3s, so an unbounded
// per-session map is fine.
const ttsClipCache = new Map<string, Blob>();
const ttsCacheKey = (voiceId: string, text: string) => `${voiceId}::${text}`;

export function RecordingModal({ open, onClose, onSave, initialScript }: RecordingModalProps) {
  const [phase, setPhase] = useState<Phase>('setup');
  const [source, setSource] = useState<'camera' | 'screen'>('camera');
  const [script, setScript] = useState('');
  // Sides usually arrive as a PDF, and on a phone there is often no way to select
  // the text at all. Upload runs it through the same parser casting uses.
  const [parsingScript, setParsingScript] = useState(false);
  const [scriptFileError, setScriptFileError] = useState<string | null>(null);
  const handleScriptFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setScriptFileError(null);
    const isPdf = file.type === 'application/pdf' || /\.pdf$/i.test(file.name);
    const isTxt = file.type === 'text/plain' || /\.txt$/i.test(file.name);
    if (!isPdf && !isTxt) { setScriptFileError('Upload a PDF or a .txt file.'); return; }
    if (file.size > 10 * 1024 * 1024) { setScriptFileError('That file is over 10MB.'); return; }
    setParsingScript(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const res = await fetch('/api/script/parse', { method: 'POST', body: form });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) throw new Error(data.error || 'Could not read that file');
      const text = String(data.text || '').trim();
      if (!text) throw new Error('No text found — a scanned PDF needs to be typed or pasted in.');
      setScript(text);
    } catch (err: any) {
      setScriptFileError(err.message);
    } finally {
      setParsingScript(false);
    }
  };
  const [fontSize, setFontSize] = useState(22);
  const [scrollSpeed, setScrollSpeed] = useState(3);
  const [takeNumber, setTakeNumber] = useState(1);
  const [recordedBlob, setRecordedBlob] = useState<Blob | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [devicesLoaded, setDevicesLoaded] = useState(false);
  const [countdown, setCountdown] = useState<number | null>(null);

  // Scene Partner state
  const [scenePartnerEnabled, setScenePartnerEnabled] = useState(false);
  const [dialogueTurns, setDialogueTurns] = useState<DialogueTurn[]>([]);
  const [characters, setCharacters] = useState<string[]>([]);
  const [selectedCharacter, setSelectedCharacter] = useState('');
  const [characterVoices, setCharacterVoices] = useState<Record<string, string>>({});
  const [readerAudioBlobs, setReaderAudioBlobs] = useState<Map<number, Blob>>(new Map());
  const [preparingPartner, setPreparingPartner] = useState(false);
  const [currentTurnIndex, setCurrentTurnIndex] = useState(0);
  const [isReaderSpeaking, setIsReaderSpeaking] = useState(false);
  const [vadEnabled, setVadEnabled] = useState(false);

  // Refs mirror state so handleSilence always reads the latest values
  // without needing to be re-created (avoids stale closure + unnecessary VAD restarts)
  const currentTurnIndexRef = useRef(0);
  const isReaderSpeakingRef = useRef(false);
  const vadPauseRef = useRef<() => void>(() => {});
  const vadResumeRef = useRef<() => void>(() => {});

  const previewVideoRef = useRef<HTMLVideoElement>(null);
  const readerAudioRef = useRef<HTMLAudioElement | null>(null);

  // Audio mixing refs for recording reader audio into the video
  const mixContextRef = useRef<AudioContext | null>(null);
  const mixDestRef = useRef<MediaStreamAudioDestinationNode | null>(null);
  const mixedStreamRef = useRef<MediaStream | null>(null);

  const {
    stream,
    isRecording,
    duration,
    error,
    videoDevices,
    audioDevices,
    selectedVideoDevice,
    selectedAudioDevice,
    setSelectedVideoDevice,
    setSelectedAudioDevice,
    deviceStatus,
    enumerateDevices,
    startCamera,
    startScreen,
    startRecording: startMediaRecording,
    stopRecording,
    cancelRecording,
    stopCamera,
  } = useMediaRecorder();

  // --- Scene Partner: parse script when it changes or modal re-opens ---
  useEffect(() => {
    if (!open || !scenePartnerEnabled || !script.trim()) {
      if (!open) {
        setDialogueTurns([]);
        setCharacters([]);
        setSelectedCharacter('');
      }
      return;
    }

    let cancelled = false;

    // Wait for a pause in typing before parsing: the parse is one model call
    // per run, so firing on every keystroke made one editing session cost
    // hundreds of calls.
    const timer = setTimeout(async () => {
      let turns: DialogueTurn[];
      try {
        const token = localStorage.getItem('authToken') || localStorage.getItem('token');
        const resp = await fetch('/api/parse-script', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({ script }),
        });
        if (!resp.ok) throw new Error('API error');
        const data = await resp.json();
        turns = data.turns || [];
      } catch {
        // Fallback to local regex parser
        turns = parseScript(script);
      }

      if (cancelled) return;
      setDialogueTurns(turns);
      const chars = getCharacters(turns);
      setCharacters(chars);
      if (chars.length > 0 && !chars.includes(selectedCharacter)) {
        setSelectedCharacter(chars[0]);
      }
      // Auto-assign different voices to reader characters (round-robin)
      setCharacterVoices(prev => {
        const updated = { ...prev };
        let voiceIdx = 0;
        for (const c of chars) {
          if (!updated[c]) {
            updated[c] = VOICES[voiceIdx % VOICES.length].id;
            voiceIdx++;
          }
        }
        return updated;
      });
    }, 800);

    return () => { cancelled = true; clearTimeout(timer); };
  }, [script, scenePartnerEnabled, open]);

  // --- Scene Partner: VAD silence handler ---
  // Reads from refs (not state) so the callback is stable and recursive calls
  // always see the latest turn index — fixes stale-closure infinite-repeat bug
  const handleSilence = useCallback(() => {
    if (!scenePartnerEnabled || isReaderSpeakingRef.current) return;

    // Find the next reader turn from current position
    let nextReaderIdx = currentTurnIndexRef.current;
    // If current turn is actor's, advance to next reader turn
    while (nextReaderIdx < dialogueTurns.length && dialogueTurns[nextReaderIdx]?.character === selectedCharacter) {
      nextReaderIdx++;
    }

    if (nextReaderIdx >= dialogueTurns.length) return; // Script finished

    const blob = readerAudioBlobs.get(nextReaderIdx);
    if (!blob) {
      // No audio for this turn, skip to next
      currentTurnIndexRef.current = nextReaderIdx + 1;
      setCurrentTurnIndex(nextReaderIdx + 1);
      return;
    }

    // Play reader audio through AudioContext (both speakers AND recording mix)
    isReaderSpeakingRef.current = true;
    setIsReaderSpeaking(true);
    currentTurnIndexRef.current = nextReaderIdx;
    setCurrentTurnIndex(nextReaderIdx);
    vadPauseRef.current();

    const ctx = mixContextRef.current;
    const dest = mixDestRef.current;

    // Decode and play through Web Audio API so it gets mixed into the recording
    blob.arrayBuffer().then(buffer => {
      if (!ctx || !dest) return;
      return ctx.decodeAudioData(buffer);
    }).then(audioBuffer => {
      if (!ctx || !dest || !audioBuffer) return;

      const sourceNode = ctx.createBufferSource();
      sourceNode.buffer = audioBuffer;
      // Connect to speakers (so actor hears it)
      sourceNode.connect(ctx.destination);
      // Connect to mix destination (so it gets recorded)
      sourceNode.connect(dest);

      sourceNode.onended = () => {
        isReaderSpeakingRef.current = false;
        setIsReaderSpeaking(false);
        const next = nextReaderIdx + 1;
        currentTurnIndexRef.current = next;
        setCurrentTurnIndex(next);
        // If next turn is also a reader line, play it immediately (chain consecutive reader lines)
        if (next < dialogueTurns.length && dialogueTurns[next].character !== selectedCharacter) {
          setTimeout(() => handleSilence(), 50);
        } else {
          // Resume VAD — either for the actor's next line, or to clear paused
          // state when the script is finished (prevents stuck pausedRef on re-record)
          vadResumeRef.current();
        }
      };

      sourceNode.start();
    }).catch(() => {
      isReaderSpeakingRef.current = false;
      setIsReaderSpeaking(false);
      vadResumeRef.current();
    });
  }, [scenePartnerEnabled, dialogueTurns, selectedCharacter, readerAudioBlobs]);

  const { pause: vadPause, resume: vadResume } = useVoiceActivity({
    stream,
    onSilence: handleSilence,
    enabled: vadEnabled,
    silenceThreshold: 1500,
  });
  vadPauseRef.current = vadPause;
  vadResumeRef.current = vadResume;

  // --- Generate TTS audio for reader lines ---
  const generateReaderAudio = async () => {
    setPreparingPartner(true);
    const audioMap = new Map<number, Blob>();

    // Identify reader turns (non-actor lines)
    const readerTurns = dialogueTurns
      .map((turn, i) => ({ turn, i }))
      .filter(({ turn }) => turn.character !== selectedCharacter);

    try {
      const results = await Promise.all(
        readerTurns.map(async ({ turn, i }) => {
          const voiceId = characterVoices[turn.character] || 'Matthew';
          const cacheKey = ttsCacheKey(voiceId, turn.line);

          // Reuse a previously synthesized clip if we've already generated
          // this exact line + voice this session (retakes, voice tweaks).
          const cached = ttsClipCache.get(cacheKey);
          if (cached) return { i, blob: cached };

          const res = await fetch('/api/tts', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ text: turn.line, voiceId }),
          });
          if (!res.ok) throw new Error(`TTS failed for line ${i}`);
          const blob = await res.blob();
          ttsClipCache.set(cacheKey, blob);
          return { i, blob };
        })
      );

      for (const { i, blob } of results) {
        audioMap.set(i, blob);
      }
    } catch (err) {
      console.error('Failed to generate reader audio:', err);
    }

    setReaderAudioBlobs(audioMap);
    setPreparingPartner(false);
  };

  // --- Enumerate devices when modal opens ---
  useEffect(() => {
    if (open && !devicesLoaded) {
      enumerateDevices().then(() => setDevicesLoaded(true));
    }
  }, [open, devicesLoaded, enumerateDevices]);

  const initiateCountdown = useCallback(() => {
    if (isRecording || countdown !== null) return;
    setCountdown(3);
  }, [isRecording, countdown]);

  // Countdown timer: decrements every 1s, starts recording when it hits 0
  const handleStartRecordingRef = useRef<() => void>(() => {});
  useEffect(() => {
    if (countdown === null) return;
    if (countdown === 0) {
      setCountdown(null);
      handleStartRecordingRef.current();
      return;
    }
    const timer = setTimeout(() => setCountdown(c => (c !== null ? c - 1 : null)), 1000);
    return () => clearTimeout(timer);
  }, [countdown]);

  // --- Web Speech API: voice commands ("action" / "cut") ---
  const isRecordingRef = useRef(false);
  isRecordingRef.current = isRecording;
  const countdownRef = useRef<number | null>(null);
  countdownRef.current = countdown;
  const handleStopRecordingRef = useRef<() => void>(() => {});

  useEffect(() => {
    if (phase !== 'recording') return;

    const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (!SpeechRecognition) return; // Browser doesn't support it — skip silently

    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = false;
    let stopped = false;

    recognition.onresult = (event: any) => {
      const last = event.results[event.results.length - 1];
      if (!last.isFinal) return;
      const transcript = last[0].transcript.toLowerCase().trim();

      if (transcript.includes('action') && !isRecordingRef.current && countdownRef.current === null) {
        initiateCountdown();
      } else if (transcript.includes('cut') && isRecordingRef.current) {
        handleStopRecordingRef.current();
      }
    };

    recognition.onend = () => {
      // Chrome stops recognition periodically — restart unless we intentionally stopped
      if (!stopped) {
        try { recognition.start(); } catch { /* already running */ }
      }
    };

    recognition.onerror = () => {
      // Silently ignore — voice commands are optional
    };

    try { recognition.start(); } catch { /* ignore */ }

    return () => {
      stopped = true;
      try { recognition.stop(); } catch { /* ignore */ }
    };
  }, [phase, initiateCountdown]);

  // Clean up preview URL
  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  // Clean up when modal closes
  useEffect(() => {
    if (!open) {
      stopCamera();
      cleanupMixer();
      setPhase('setup');
      setRecordedBlob(null);
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
      setTakeNumber(1);
      setDevicesLoaded(false);
      setCountdown(null);
      currentTurnIndexRef.current = 0;
      setCurrentTurnIndex(0);
      isReaderSpeakingRef.current = false;
      setIsReaderSpeaking(false);
      setVadEnabled(false);
      setReaderAudioBlobs(new Map());
      setPreparingPartner(false);
    } else if (initialScript) {
      // Seed the teleprompter from the concierge-generated script.
      setScript(initialScript);
    }
  }, [open, initialScript]);

  const handleStartCamera = async () => {
    // Generate reader audio if scene partner is on
    if (scenePartnerEnabled && dialogueTurns.length > 0) {
      await generateReaderAudio();
    }
    // Only move to the recording screen if the camera actually opened. This used
    // to advance no matter what, so a blocked or busy camera dropped people onto a
    // black recording view while the error stayed behind on the setup screen.
    const started = await startCamera(selectedVideoDevice, selectedAudioDevice);
    if (started) setPhase('recording');
  };

  const handleStartScreen = async () => {
    try {
      await startScreen({ withWebcam: true });
      setPhase('recording');
    } catch { /* share cancelled/denied — stay on setup, error is shown */ }
  };

  const handleStartRecording = () => {
    if (scenePartnerEnabled && stream && dialogueTurns.length > 0) {
      // Set up audio mixer: mic + reader audio → mixed stream for recording
      const ctx = new AudioContext();
      const dest = ctx.createMediaStreamDestination();

      // Connect mic audio to the mix
      const micSource = ctx.createMediaStreamSource(stream);
      micSource.connect(dest);

      // Build mixed stream: video from camera + audio from mix destination
      const videoTrack = stream.getVideoTracks()[0];
      const mixedAudioTrack = dest.stream.getAudioTracks()[0];
      const mixedStream = new MediaStream([videoTrack, mixedAudioTrack]);

      mixContextRef.current = ctx;
      mixDestRef.current = dest;
      mixedStreamRef.current = mixedStream;

      // Record from the mixed stream so reader audio is captured
      startMediaRecording(mixedStream);
      currentTurnIndexRef.current = 0;
      setCurrentTurnIndex(0);
      setVadEnabled(true);

      // Auto-start: if first line is a reader line, play it immediately
      if (dialogueTurns[0]?.character !== selectedCharacter) {
        setTimeout(() => handleSilence(), 100);
      }
    } else {
      startMediaRecording();
    }
  };

  const cleanupMixer = () => {
    if (mixContextRef.current) {
      mixContextRef.current.close();
      mixContextRef.current = null;
    }
    mixDestRef.current = null;
    mixedStreamRef.current = null;
  };

  const handleStopRecording = async () => {
    setVadEnabled(false);
    isReaderSpeakingRef.current = false;
    setIsReaderSpeaking(false);
    cleanupMixer();

    const blob = await stopRecording();
    setRecordedBlob(blob);
    const url = URL.createObjectURL(blob);
    setPreviewUrl(url);
    setPhase('review');
  };

  // Keep refs in sync so countdown/speech effects can call these without stale closures
  handleStartRecordingRef.current = handleStartRecording;
  handleStopRecordingRef.current = handleStopRecording;

  const createFile = useCallback((blob: Blob, take: number): File => {
    const ext = blob.type.includes('mp4') ? 'mp4' : 'webm';
    return new File([blob], `self-tape-take-${take}.${ext}`, { type: blob.type || 'video/webm' });
  }, []);

  const handleDiscard = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    setRecordedBlob(null);
    currentTurnIndexRef.current = 0;
    setCurrentTurnIndex(0);
    isReaderSpeakingRef.current = false;
    setIsReaderSpeaking(false);
    setPhase('recording');
  };

  const handleSaveAndRecordAnother = () => {
    if (recordedBlob) {
      onSave(createFile(recordedBlob, takeNumber));
      setTakeNumber(n => n + 1);
    }
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    setRecordedBlob(null);
    currentTurnIndexRef.current = 0;
    setCurrentTurnIndex(0);
    isReaderSpeakingRef.current = false;
    setIsReaderSpeaking(false);
    setPhase('recording');
  };

  const handleContinue = () => {
    if (recordedBlob) {
      onSave(createFile(recordedBlob, takeNumber));
    }
    onClose();
  };

  const handleDownload = async () => {
    if (!recordedBlob) return;
    const file = createFile(recordedBlob, takeNumber);

    // iOS: use Web Share API which offers "Save Video" to camera roll
    if (/iPhone|iPad/i.test(navigator.userAgent) && navigator.canShare?.({ files: [file] })) {
      try {
        await navigator.share({ files: [file], title: file.name });
        return;
      } catch {
        // User cancelled or share failed — fall through to download
      }
    }

    // Desktop / Android: standard download
    const url = URL.createObjectURL(recordedBlob);
    const a = document.createElement('a');
    a.href = url;
    a.download = file.name;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleModalClose = () => {
    stopCamera();
    onClose();
  };

  const scenePartnerReady = !scenePartnerEnabled || (characters.length > 0 && selectedCharacter);

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) handleModalClose(); }}>
      <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto p-0 sm:top-[5%] sm:translate-y-0">
        {/* Setup Phase */}
        {phase === 'setup' && (
          <div className="p-6 space-y-5">
            {/* Source: camera self-tape vs screen + webcam bubble */}
            <div className="grid grid-cols-2 gap-1 p-1 rounded-lg bg-muted/40">
              {([['camera', 'Camera'], ['screen', 'Screen + you']] as const).map(([val, label]) => (
                <button
                  key={val}
                  type="button"
                  onClick={() => { setSource(val); if (val === 'screen') setScenePartnerEnabled(false); }}
                  className={`py-2 text-sm font-medium rounded-md transition-colors ${source === val ? 'bg-background shadow-sm' : 'text-muted-foreground'}`}
                >
                  {label}
                </button>
              ))}
            </div>

            {source === 'camera' && (
            <>
            <div>
              <h2 className="text-lg font-semibold">Record with Teleprompter</h2>
              <p className="text-sm text-muted-foreground mt-1">
                Paste your script below, or upload the sides as a PDF. It will scroll on screen while you record — but won't appear in the video.
              </p>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between gap-3">
                <Label htmlFor="script">Script</Label>
                <label className="cursor-pointer inline-flex items-center gap-1.5 text-xs font-medium" style={{ color: 'var(--hj-ink-2)' }}>
                  {parsingScript ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                  {parsingScript ? 'Reading…' : 'Upload a PDF'}
                  <input type="file" accept=".pdf,.txt,application/pdf,text/plain" className="hidden" onChange={handleScriptFile} disabled={parsingScript} />
                </label>
              </div>
              {scriptFileError && (
                <p className="m-0 text-xs" style={{ color: 'hsl(var(--destructive))' }}>{scriptFileError}</p>
              )}
              <Textarea
                id="script"
                placeholder="Paste your script or sides here..."
                value={script}
                onChange={(e) => setScript(e.target.value)}
                onKeyDown={(e) => {
                  // Safari: allow Enter to dismiss keyboard and scroll to next fields
                  if (e.key === 'Enter' && !e.shiftKey && script.trim()) {
                    // Don't prevent default — let newline work
                  }
                }}
                rows={4}
                className="resize-y text-base"
                style={{ fontSize: '16px' }} // Prevents Safari auto-zoom on focus
              />
              {/* Safari mobile: explicit Done button to dismiss keyboard */}
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="sm:hidden text-xs text-muted-foreground"
                onClick={() => {
                  // Blur the textarea to dismiss Safari keyboard
                  (document.activeElement as HTMLElement)?.blur();
                }}
              >
                Done editing script
              </Button>
            </div>

            {/* Scene Partner Toggle */}
            <div className="flex items-center justify-between p-3 rounded-lg border bg-muted/30">
              <div className="flex items-center gap-2">
                <Users className="w-4 h-4 text-muted-foreground" />
                <div>
                  <Label htmlFor="scene-partner" className="cursor-pointer">Scene Partner</Label>
                  <p className="text-xs text-muted-foreground">AI reads the other character's lines</p>
                </div>
              </div>
              <Switch
                id="scene-partner"
                checked={scenePartnerEnabled}
                onCheckedChange={setScenePartnerEnabled}
              />
            </div>

            {/* Scene Partner Options */}
            {scenePartnerEnabled && (
              <div className="space-y-3 p-3 rounded-lg border border-dashed">
                <div className="space-y-2">
                  <Label>Your Character</Label>
                  {characters.length > 0 ? (
                    <Select value={selectedCharacter} onValueChange={setSelectedCharacter}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select your character" />
                      </SelectTrigger>
                      <SelectContent>
                        {characters.map(c => (
                          <SelectItem key={c} value={c}>{c}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <p className="text-sm text-muted-foreground">
                      {script.trim() ? 'No characters detected — use ALL CAPS character names' : 'Enter a script above'}
                    </p>
                  )}
                </div>
                {/* Per-character voice assignment */}
                {characters.filter(c => c !== selectedCharacter).length > 0 && (
                  <div className="space-y-2">
                    <Label className="text-xs text-muted-foreground">Reader Voices</Label>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {characters.filter(c => c !== selectedCharacter).map(c => (
                        <div key={c} className="flex items-center gap-2">
                          <span className="text-xs font-medium text-blue-400 min-w-0 truncate flex-shrink-0" style={{ maxWidth: '120px' }}>
                            {c}
                          </span>
                          <Select
                            value={characterVoices[c] || 'Matthew'}
                            onValueChange={(v) => setCharacterVoices(prev => ({ ...prev, [c]: v }))}
                          >
                            <SelectTrigger className="h-8 text-xs flex-1">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {VOICES.map(v => (
                                <SelectItem key={v.id} value={v.id}>{v.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Camera & Mic Selection */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="flex items-center gap-1.5">
                  <Camera className="w-3.5 h-3.5" /> Camera
                </Label>
                {videoDevices.length > 0 ? (
                  <Select value={selectedVideoDevice} onValueChange={setSelectedVideoDevice}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select camera" />
                    </SelectTrigger>
                    <SelectContent>
                      {videoDevices.map(d => (
                        <SelectItem key={d.deviceId} value={d.deviceId}>
                          {d.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : deviceStatus === 'requesting' ? (
                  <p className="text-sm text-muted-foreground flex items-center gap-2">
                    <Loader2 className="w-3.5 h-3.5 animate-spin" /> Looking for cameras…
                  </p>
                ) : (
                  <div className="space-y-2">
                    <p className="text-sm text-destructive">
                      {error || 'No camera available yet.'}
                    </p>
                    <Button type="button" variant="outline" size="sm" onClick={() => { setDevicesLoaded(false); }}>
                      Try again
                    </Button>
                  </div>
                )}
              </div>
              <div className="space-y-2">
                <Label className="flex items-center gap-1.5">
                  <Mic className="w-3.5 h-3.5" /> Microphone
                </Label>
                {audioDevices.length > 0 ? (
                  <Select value={selectedAudioDevice} onValueChange={setSelectedAudioDevice}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select microphone" />
                    </SelectTrigger>
                    <SelectContent>
                      {audioDevices.map(d => (
                        <SelectItem key={d.deviceId} value={d.deviceId}>
                          {d.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : deviceStatus === 'requesting' ? (
                  <p className="text-sm text-muted-foreground">Looking for microphones…</p>
                ) : (
                  <p className="text-sm text-muted-foreground">No microphone found. You can still record video.</p>
                )}
              </div>
            </div>

            {/* Teleprompter Settings */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Font Size: {fontSize}px</Label>
                <Slider
                  value={[fontSize]}
                  onValueChange={([v]) => setFontSize(v)}
                  min={14}
                  max={32}
                  step={1}
                />
              </div>
              <div className="space-y-2">
              <Label>Scroll Speed: {scrollSpeed}</Label>
              <Slider
                value={[scrollSpeed]}
                onValueChange={([v]) => setScrollSpeed(v)}
                min={1}
                max={10}
                step={1}
              />
            </div>
            </div>

            {error && (
              <p className="text-sm text-destructive">{error}</p>
            )}

            <Button
              onClick={handleStartCamera}
              className="w-full"
              disabled={preparingPartner || !scenePartnerReady}
            >
              {preparingPartner ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Preparing scene partner...</>
              ) : (
                <><Video className="w-4 h-4 mr-2" /> Start Camera</>
              )}
            </Button>
            </>
            )}

            {source === 'screen' && (
              <div className="space-y-4">
                <div>
                  <h2 className="text-lg font-semibold">Screen recording</h2>
                  <p className="text-sm text-muted-foreground mt-1">
                    Share a window or tab and we'll record it with your webcam in a bubble in the corner — great for walkthroughs and course lessons.
                  </p>
                </div>
                <div className="space-y-2">
                  <Label className="flex items-center gap-1.5"><Mic className="w-3.5 h-3.5" /> Microphone</Label>
                  {audioDevices.length > 0 ? (
                    <Select value={selectedAudioDevice} onValueChange={setSelectedAudioDevice}>
                      <SelectTrigger><SelectValue placeholder="Select microphone" /></SelectTrigger>
                      <SelectContent>
                        {audioDevices.map(d => <SelectItem key={d.deviceId} value={d.deviceId}>{d.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  ) : (
                    <p className="text-sm text-muted-foreground">Detecting microphones...</p>
                  )}
                  <p className="text-xs text-muted-foreground">Tip: share a Chrome <em>tab</em> (not the whole screen) to capture its audio too.</p>
                </div>
                {error && <p className="text-sm text-destructive">{error}</p>}
                <Button onClick={handleStartScreen} className="w-full">
                  <Video className="w-4 h-4 mr-2" /> Share screen
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Recording Phase */}
        {phase === 'recording' && stream && (
          <div className="space-y-3">
            <div className="px-4 pt-4 flex items-center justify-between">
              <h2 className="text-lg font-semibold">Take {takeNumber}</h2>
              <div className="flex items-center gap-2">
                {isRecording && (
                  <div className="flex items-center gap-1.5 bg-black/60 px-2.5 py-1 rounded-full">
                    <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                    <span className="text-sm font-mono text-red-400">{Math.floor(duration / 60).toString().padStart(2, '0')}:{(duration % 60).toString().padStart(2, '0')}</span>
                  </div>
                )}
                {error && <p className="text-sm text-destructive">{error}</p>}
              </div>
            </div>
            <CameraPreview
              stream={stream}
              isRecording={isRecording}
              duration={duration}
              script={script}
              scrollSpeed={scrollSpeed}
              fontSize={fontSize}
              onScrollSpeedChange={setScrollSpeed}
              onStartRecording={initiateCountdown}
              onStopRecording={handleStopRecording}
              onCancel={handleModalClose}
              countdown={countdown}
              dialogueTurns={scenePartnerEnabled ? dialogueTurns : undefined}
              currentTurnIndex={scenePartnerEnabled ? currentTurnIndex : undefined}
              actorCharacter={scenePartnerEnabled ? selectedCharacter : undefined}
              isReaderSpeaking={isReaderSpeaking}
            />
          </div>
        )}

        {/* Review Phase */}
        {phase === 'review' && previewUrl && (
          <div className="space-y-4">
            <div className="px-4 pt-4">
              <h2 className="text-lg font-semibold">Review Take {takeNumber}</h2>
            </div>

            <video
              ref={previewVideoRef}
              src={previewUrl}
              controls
              className="w-full bg-black rounded-none"
              style={{ maxHeight: '70vh' }}
            />

            <div className="px-4 pb-4 flex flex-col sm:flex-row gap-2">
              <Button variant="outline" onClick={handleDiscard} className="flex-1">
                <RotateCcw className="w-4 h-4 mr-2" /> Discard & Re-record
              </Button>
              <Button variant="outline" onClick={handleDownload} className="flex-1">
                {/iPhone|iPad/i.test(navigator.userAgent)
                  ? <><Share2 className="w-4 h-4 mr-2" /> Save to Camera Roll</>
                  : <><Download className="w-4 h-4 mr-2" /> Download</>
                }
              </Button>
              <Button variant="outline" onClick={handleSaveAndRecordAnother} className="flex-1">
                <Save className="w-4 h-4 mr-2" /> Save & Record Another
              </Button>
              <Button onClick={handleContinue} className="flex-1">
                Continue <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
